import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@mytours/components'

import ProgressRing from '../../offline/components/ProgressRing'
import { percentComplete } from '../../offline/functions'
import { useCollectionDownload } from '../../offline/useDownload'
import strings from '../../../strings'
import {
	dismissDownloadPill,
	downloadPillDismissed,
	downloadPillLabel,
	downloadRowState,
	WebDownloadPolicy,
} from '../functions'

import './styles/download.less'

interface Props {
	collectionId: number
	/** `off` never reaches here; the page renders no control at all. */
	policy: Exclude<WebDownloadPolicy, 'off'>
	/** The published size, which is native's figure and overstates. A label, not a budget. */
	fileSize?: number
	/** The language the visitor is reading in, which a saved tour has to match to count as saved. */
	language: string
	/** What to call this download where a visitor manages their storage. */
	title?: string
}

/**
 * The single page layout's offer of an offline copy, floating over the stops.
 *
 * Every other layout hangs its download off the footer's Start tour button — see TourDownload. This one
 * has no such button, because its stops are already on the page, so the offer has to stand on its own
 * and say what it is rather than being a glyph beside something else.
 *
 * It arrives as a pill at the foot of the page, saying what a download costs or how far a forced one has
 * got. Dismissing it collapses it to a circle above the stops, which carries the same press and the same
 * progress in the space of a glyph. The dismissal is remembered per tour, so a visitor puts the pill away
 * once rather than once a visit. A tour already saved offers neither: the heading's Downloaded chip is
 * the whole of what there is left to say — see TourDownloadedChip.
 */
const SinglePageTourDownload: React.FC<Props> = ({ collectionId, policy, fileSize, language, title }) => {
	const [collapsed, setCollapsed] = useState(() => downloadPillDismissed(collectionId))
	const autoStarted = useRef(false)
	const {
		status,
		progress,
		language: savedLanguage,
		supported,
		loaded,
		start,
		cancel,
		retryNow,
	} = useCollectionDownload(collectionId, title)

	const state = downloadRowState(status, savedLanguage, language)
	const { downloading, failed, saved } = state
	const percent = percentComplete(progress)

	// `forced` elsewhere is a gate in front of the only way in. Here there is no way in to stand in front
	// of, so it downloads on arrival and leaves the stops readable while it runs. Once only, and never
	// before the registry has been read: restarting a download the visitor cancelled would make the
	// cancel button do nothing, and acting on an unread `none` would re-run a tour already saved.
	useEffect(() => {
		if (autoStarted.current || policy !== 'forced' || !supported || !loaded || status !== 'none') return

		autoStarted.current = true
		start()
	}, [policy, supported, loaded, status, start])

	// A browser with no service worker or Cache API — an in-app browser, a private window — has nowhere to
	// download into, so it is offered nothing rather than a control that cannot work.
	if (!supported || saved) return null

	const press = () => {
		if (downloading) cancel()
		else if (failed) retryNow()
		else start()
	}

	const dismiss = () => {
		dismissDownloadPill(collectionId)
		setCollapsed(true)
	}

	const label = downloading ? `${strings.downloading_tour} ${percent}%` : downloadPillLabel(state, fileSize)

	const glyph = downloading ? (
		<span className="tour-download-ring">
			<ProgressRing percent={percent} />
			{/* The ring says a download is running; the square inside it is what says the press stops one. */}
			<span className="tour-download-stop" aria-hidden="true" />
		</span>
	) : (
		<i className={`fal fa-${failed ? 'circle-exclamation' : 'arrow-down-to-line'}`} aria-hidden="true" />
	)

	if (collapsed) {
		return (
			<Button variant="clear" className="tour-download-fab" aria-label={label} onClick={press}>
				{glyph}
			</Button>
		)
	}

	return (
		<div className="tour-download-pill">
			<Button variant="clear" className="tour-download-pill-action" aria-label={label} onClick={press}>
				{glyph}
				<span className="tour-download-pill-label">{label}</span>
			</Button>
			<Button variant="clear" className="tour-download-dismiss" aria-label={strings.close} onClick={dismiss}>
				<i className="fal fa-xmark" aria-hidden="true" />
			</Button>
		</div>
	)
}

export default SinglePageTourDownload
